import { SCHOOL_THEME } from '@constants/storages'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { isWeb } from 'tamagui'
import { create } from 'zustand'
import { IThemeSeed } from '../theme/buildTheme'
import { resolveSchoolThemeSeed } from '../theme/schoolThemes'

interface ICachedSchoolTheme {
  host: string
  seed: IThemeSeed
}

interface ISchoolThemeStore {
  seed: IThemeSeed | null
  /** A escola já respondeu; a partir daqui o cache não sobrescreve mais. */
  resolved: boolean
  /** Nativo: há sessão ativa. Sem sessão, a escola salva no aparelho não vale. */
  sessionActive: boolean
  /** Nativo: tema da escola escolhida na tela de login, antes de haver sessão. */
  loginSeed: IThemeSeed | null
  setSchoolTheme: (host: string, seed: IThemeSeed | null) => void
  setSessionActive: (sessionActive: boolean) => void
  /** Nativo: escola escolhida na tela de login (null ao voltar ao e-mail). */
  setLoginSchool: (schoolId: number | string | null | undefined) => void
}

type ISchoolThemeState = Pick<
  ISchoolThemeStore,
  'seed' | 'sessionActive' | 'loginSeed'
>

/**
 * Tema que a tela deve usar. Na web a escola vem do domínio, então vale desde o
 * login. No app a escola vem do que ficou salvo no aparelho, que sobrevive à
 * queda da sessão: sem sessão vale só a escola escolhida no login.
 */
export function activeSchoolSeed(
  state: ISchoolThemeState,
  web: boolean = isWeb,
): IThemeSeed | null {
  if (web || state.sessionActive) return state.seed

  return state.loginSeed
}

function parseCached(raw: string | null, host: string): IThemeSeed | null {
  if (!raw) return null
  try {
    const cached = JSON.parse(raw) as ICachedSchoolTheme
    return cached?.host === host && cached.seed ? cached.seed : null
  } catch {
    return null
  }
}

// Na web o cache é lido de forma síncrona para o primeiro render já sair com o
// tema da escola, sem piscar o default enquanto os parâmetros carregam.
function readWebCache(): IThemeSeed | null {
  if (!isWeb || typeof window === 'undefined') return null
  try {
    return parseCached(
      window.localStorage.getItem(SCHOOL_THEME),
      window.location.hostname,
    )
  } catch {
    return null
  }
}

export const useSchoolThemeStore = create<ISchoolThemeStore>((set) => ({
  seed: readWebCache(),
  resolved: false,
  sessionActive: false,
  loginSeed: null,
  setSessionActive: (sessionActive) => set({ sessionActive }),
  setLoginSchool: (schoolId) =>
    set({ loginSeed: resolveSchoolThemeSeed(schoolId) }),
  setSchoolTheme: (host, seed) => {
    set({ seed, resolved: true })
    const persisted = seed
      ? AsyncStorage.setItem(SCHOOL_THEME, JSON.stringify({ host, seed }))
      : AsyncStorage.removeItem(SCHOOL_THEME)
    persisted.catch(() => undefined)
  },
}))

/** No nativo o cache é async: aplica só se a escola ainda não respondeu. */
export async function hydrateSchoolThemeStore(host: string) {
  if (!host) return
  try {
    const seed = parseCached(await AsyncStorage.getItem(SCHOOL_THEME), host)
    if (!seed || useSchoolThemeStore.getState().resolved) return
    useSchoolThemeStore.setState({ seed })
  } catch {
    // cache ilegível: segue no default até a escola responder
  }
}
